/**
 * Authentication Service
 * POV-0013: Frontend login + session handling
 */
import api from './api';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const REMEMBER_ME_KEY = 'remember_me';

class AuthService {
  /**
   * Login with email and password
   * @param {string} email - User's email address
   * @param {string} password - Password
   * @param {boolean} rememberMe - If true, session lasts 30 days; otherwise 15 minutes
   */
  async login(email, password, rememberMe = false) {
    const response = await api.post('/api/v1/auth/login', {
      email,
      password,
      remember_me: rememberMe,
    });

    const { access_token, refresh_token, user } = response.data;

    // Store tokens and user
    this.setTokens(access_token, refresh_token);
    this.setUser(user);
    
    // Store remember me preference
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }

    return response.data;
  }

  /**
   * Refresh the access token
   */
  async refreshToken() {
    const refresh_token = this.getRefreshToken();
    
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    try {
      const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
      const response = await api.post('/api/v1/auth/refresh', {
        refresh_token,
        remember_me: rememberMe,
      });

      const { access_token, refresh_token: newRefreshToken, user } = response.data;
      
      this.setTokens(access_token, newRefreshToken);
      this.setUser(user);

      return response.data;
    } catch (error) {
      // If refresh fails, logout
      this.logout();
      throw error;
    }
  }

  /**
   * Logout and clear stored data
   * Calls backend API to terminate session, then clears local storage
   */
  async logout() {
    try {
      // Call backend to terminate the session
      const token = this.getToken();
      if (token) {
        await api.post('/api/v1/auth/logout');
      }
    } catch (error) {
      // Even if API call fails, proceed with local cleanup
      console.warn('Backend logout failed:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  }

  /**
   * Get current user profile from API
   */
  async getCurrentUser() {
    const response = await api.get('/api/v1/auth/me');
    const user = response.data;
    this.setUser(user);
    return user;
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Get stored access token
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user data
   */
  getUser() {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Check if remember me is enabled
   */
  isRememberMeEnabled() {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  }

  /**
   * Store tokens
   */
  setTokens(accessToken, refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Store user data
   */
  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role) {
    const user = this.getUser();
    if (!user || !user.role) return false;
    return user.role === role || user.role === 'admin';
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles) {
    const user = this.getUser();
    if (!user || !user.role) return false;
    if (user.role === 'admin') return true;
    return roles.includes(user.role);
  }

  /**
   * Get available roles
   */
  async getRoles() {
    const response = await api.get('/api/v1/auth/roles');
    return response.data.roles;
  }
}

export const authService = new AuthService();
export default authService;
